/**
 * BusinessTrackNudge — dashboard banner shown to users who registered
 * with account_type='business' (or 'company') BUT haven't yet been
 * approved as a Business Suite customer (i.e. role !== 'business_suite').
 *
 * Three states:
 *   1. No application yet → "Apply for Business Suite" CTA → /dashboard/business-suite/apply
 *   2. Application pending/reviewing/follow_up → status pill + ETA
 *   3. Application rejected → terse note + link to view notes
 *
 * Quietly returns null when:
 *   - user.role === 'business_suite' (already approved)
 *   - user.account_type not in business set (personal track)
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { authedFetch } from '@/lib/auth';
import { Building2, AlertCircle, CheckCircle2, ArrowRight, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  pending:    { en: 'Application pending review',    ja: '審査待ち',    color: 'amber' },
  reviewing:  { en: 'Application under review',      ja: '審査待ち',      color: 'blue' },
  follow_up:  { en: 'Action required — check email', ja: '審査待ち',    color: 'amber' },
  approved:   { en: 'Approved',                      ja: '承認済み',    color: 'emerald' },
  rejected:   { en: 'Application not approved',      ja: '非承認',      color: 'rose' },
};

const DISMISS_KEY = 'jtr_business_nudge_dismissed_at';
const DISMISS_TTL_DAYS = 7;

export default function BusinessTrackNudge({ user }) {
  const { lang } = useLanguage();
  const ja = lang === 'ja';
  const [app, setApp] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const isBusinessIntent = ['business', 'company'].includes((user?.account_type || '').toLowerCase());
  const alreadyApproved = user?.role === 'business_suite';
  // Iter 318 — Hide the business-track nudge once ETC Usage Inquiry Service (MEISAI) is connected.
  // A customer who is already operating doesn't need a persistent
  // "Apply for Business" banner dominating their dashboard. They can
  // request a track change from Settings → Account Type.
  const meisaiConnected = (user?.meisai_connection_status === 'active');

  useEffect(() => {
    if (!isBusinessIntent || alreadyApproved) {
      setLoaded(true);
      return;
    }
    // Honour the 7-day dismiss (per-browser, per-user not necessary —
    // this is a soft nudge, not a hard gate)
    try {
      const ts = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
      if (ts && Date.now() - ts < DISMISS_TTL_DAYS * 86400_000) {
        setDismissed(true);
      }
    } catch { /* ignore */ }

    (async () => {
      try {
        const r = await authedFetch(`${API}/api/business-suite/my-application`, { credentials: 'include' });
        if (r.ok) {
          const data = await r.json();
          setApp(data?.application || null);
        }
      } catch { /* ignore */ }
      setLoaded(true);
    })();
  }, [isBusinessIntent, alreadyApproved]);

  if (!isBusinessIntent || alreadyApproved || meisaiConnected || !loaded || dismissed) {
    return null;
  }

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setDismissed(true);
  };

  // ─ State 1: no application yet ─
  if (!app) {
    return (
      <div
        className="mb-4 rounded-2xl border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 via-white to-emerald-50 p-4 sm:p-5"
        data-testid="business-track-nudge-no-app"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
            <Building2 className="w-5 h-5" strokeWidth={2.2} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-700 mb-1">
              {ja ? '法人プラン' : 'Business Track'}
            </p>
            <p className="text-sm sm:text-base font-bold text-[#0A192F] leading-snug">
              {ja
                ? '登録時に法人を選択されました。Business Suiteをお申し込みいただくと、20枚以上のETCカードを一括管理できます。'
                : 'You selected the business track at signup. Complete your Business Suite application to manage 20+ ETC cards as a fleet.'}
            </p>
            <Link
              to="/dashboard/business-suite/apply"
              className="inline-flex items-center gap-1.5 mt-3 px-4 h-9 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold transition-colors"
              data-testid="business-track-nudge-apply-btn"
            >
              {ja ? '法人で申し込む' : 'Apply for Business'}
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.6} />
            </Link>
            <Link
              to="/dashboard/settings#account-type"
              className="inline-flex items-center ml-2 mt-3 text-xs font-semibold text-emerald-800/80 hover:text-emerald-900 underline underline-offset-2 transition-colors"
              data-testid="business-track-nudge-switch-personal"
            >
              {ja ? '法人ではない場合：個人プランに切り替え' : 'Not a business? Switch to Personal'}
            </Link>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label={ja ? '閉じる' : 'Dismiss'}
            data-testid="business-track-nudge-dismiss"
            className="text-gray-400 hover:text-gray-700 p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // ─ State 2+3: existing application ─
  const status = app.status || 'pending';
  const meta = STATUS_LABELS[status] || STATUS_LABELS.pending;
  const colorMap = {
    amber: { border: 'border-amber-300', bg: 'from-amber-50 via-white to-amber-50', chip: 'bg-amber-100 text-amber-800', Icon: AlertCircle, iconBg: 'bg-amber-600' },
    blue:  { border: 'border-blue-300',  bg: 'from-blue-50 via-white to-blue-50',   chip: 'bg-blue-100 text-blue-800',   Icon: AlertCircle, iconBg: 'bg-blue-600' },
    emerald:{ border: 'border-emerald-300', bg: 'from-emerald-50 via-white to-emerald-50', chip: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle2, iconBg: 'bg-emerald-600' },
    rose:  { border: 'border-rose-300',  bg: 'from-rose-50 via-white to-rose-50',   chip: 'bg-rose-100 text-rose-800',   Icon: AlertCircle, iconBg: 'bg-rose-600' },
  }[meta.color] || {};

  const Icon = colorMap.Icon || AlertCircle;

  return (
    <div
      className={`mb-4 rounded-2xl border-2 ${colorMap.border} bg-gradient-to-r ${colorMap.bg} p-4 sm:p-5`}
      data-testid={`business-track-nudge-${status}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl ${colorMap.iconBg} text-white flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-5 h-5" strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-600">
              {ja ? 'Business Suite 申し込み' : 'Business Suite Application'}
            </p>
            <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${colorMap.chip}`}>
              {ja ? meta.ja : meta.en}
            </span>
          </div>
          <p className="text-sm font-bold text-[#0A192F] truncate">
            {app.business_name || (ja ? '名称未設定' : 'Unnamed')}
            {' · '}
            <span className="text-gray-600 font-normal">
              {ja ? `${app.requested_card_count}枚` : `${app.requested_card_count} cards`}
            </span>
          </p>
          {status === 'follow_up' && (
            <p className="text-xs text-amber-800 mt-1">
              {ja
                ? '担当チームから確認のメールをお送りしました。受信トレイをご確認ください。'
                : 'Our team emailed you a clarification request. Please check your inbox.'}
            </p>
          )}
          {status === 'rejected' && app.decision_notes && (
            <p className="text-xs text-rose-800 mt-1 whitespace-pre-wrap line-clamp-3">
              {app.decision_notes}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label={ja ? '閉じる' : 'Dismiss'}
          data-testid="business-track-nudge-dismiss"
          className="text-gray-400 hover:text-gray-700 p-1"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
